import type { PhotoFocus, PhotoRect } from './monsterPhotoSheets'

/**
 * Paquete VANGUARD_MONSTRUOS_EXTRA (3 láminas JPG de 1536 × 1536, cuadrícula 4 × 4, celdas de 384 px).
 * Complementa las 10 láminas VANGUARD_MOBS_LIMPIOS, que se conservan sin cambios.
 *
 * - Cada foto se asocia a los IDs EXACTOS de 06_monsters impresos bajo ella ("ID: 340, 440").
 *   Varios IDs pueden compartir foto: cada ID conserva su propia ficha, estadísticas, mapas, drops y eventos.
 * - Una foto compartida por varios IDs se marca como referencia de familia (misma apariencia, no la misma entidad).
 * - VANGUARD_MONSTRUOS_INDICE_COMPACTO.json no llegó adjunto (ruta temporal del navegador): los rectángulos se
 *   midieron sobre las láminas recibidas; source_page, match_method y family_reference del índice quedan pendientes.
 * - Las fotos identifican la apariencia; no son capturas del cliente Vanguard ni confirman drops, ubicaciones o estadísticas.
 */
export const EXTRA_SHEET_SIZE = 1536
const CELL = 384

export const extraMonsterSheets: Record<number, string> = {
  1: 'https://cdn.magicpatterns.com/uploads/o1Jw6Lye12bbwkq8hDJqSW/VANGUARD_MONSTRUOS_EXTRA_01.jpg',
  2: 'https://cdn.magicpatterns.com/uploads/m3CQoJzXms1LxiTAsryogJ/VANGUARD_MONSTRUOS_EXTRA_02.jpg',
  3: 'https://cdn.magicpatterns.com/uploads/2HLMLEyN8yAMZZTTPQrHtu/VANGUARD_MONSTRUOS_EXTRA_03.jpg',
}

type Cell = { ids: number[]; name: string; focus?: PhotoFocus; rect?: PhotoRect }

/** Por lámina, fila a fila (4 por fila), en el orden impreso. */
const layout: Record<number, Cell[]> = {
  1: [
    { ids: [12, 532], name: 'Larva', focus: { fx: 0.36, fy: 0.6, zoom: 1.5 } },
    { ids: [16, 84, 90, 96, 113, 119, 125, 138, 428], name: 'Chief Skeleton Warrior', focus: { fx: 0.55, fy: 0.5, zoom: 1 } },
    { ids: [86, 92, 98, 115, 121, 127, 140, 430], name: 'Dark Skull Soldier' },
    { ids: [88, 94, 111, 117, 123, 129, 142, 432], name: 'Red Skeleton Knight', focus: { fx: 0.52, fy: 0.5, zoom: 1 } },
    { ids: [89, 95, 112, 118, 124, 130, 143, 433], name: 'Magic Skeleton', focus: { fx: 0.47, fy: 0.5, zoom: 1 } },
    { ids: [144, 174, 182, 190, 260, 268, 334], name: 'Death Angel', focus: { fx: 0.56, fy: 0.5, zoom: 1 } },
    { ids: [145, 175, 183, 191, 261, 269, 336], name: 'Death Centurion' },
    { ids: [146, 176, 184, 192, 262, 270, 333], name: 'Blood Soldier', focus: { fx: 0.56, fy: 0.5, zoom: 1 } },
    { ids: [147, 177, 185, 193, 263, 271, 331], name: 'Aegis' },
    { ids: [148, 178, 186, 194, 264, 272, 332], name: 'Rogue Centurion', focus: { fx: 0.47, fy: 0.5, zoom: 1 } },
    { ids: [149, 179, 187, 195, 265, 273, 335], name: 'Necron', focus: { fx: 0.47, fy: 0.5, zoom: 1 } },
    { ids: [160, 180, 188, 196, 266, 274, 337], name: 'Schriker', focus: { fx: 0.56, fy: 0.5, zoom: 1 } },
    { ids: [161, 181, 189, 197, 267, 338], name: 'Illusion of Kundun', focus: { fx: 0.45, fy: 0.5, zoom: 1 } },
    { ids: [313], name: 'Werewolf (Lvl 118)' },
    { ids: [316, 344], name: 'Balram', focus: { fx: 0.45, fy: 0.5, zoom: 1 } },
    { ids: [317], name: 'Soram' },
  ],
  2: [
    { ids: [340, 440], name: 'Dark Elf', focus: { fx: 0.55, fy: 0.5, zoom: 1 } },
    { ids: [350, 435], name: 'Berserker', focus: { fx: 0.56, fy: 0.5, zoom: 1 } },
    { ids: [356, 434], name: 'Gigantis' },
    { ids: [358, 438], name: 'Persona' },
    { ids: [360, 439], name: 'Dread Fear' },
    { ids: [362], name: 'Hand of Maya (left)' },
    { ids: [363], name: 'Hand of Maya (right)' },
    { ids: [409, 436], name: 'Balram (Trainee Soldier)' },
    { ids: [410], name: 'Death Spirit (Trainee Soldier)', focus: { fx: 0.47, fy: 0.5, zoom: 1 } },
    { ids: [411, 437], name: 'Soram (Trainee Soldier)' },
    { ids: [412], name: 'Dark Elf (Trainee Soldier)', focus: { fx: 0.55, fy: 0.5, zoom: 1 } },
    { ids: [420], name: 'Werewolf (Lvl 24)', focus: { fx: 0.55, fy: 0.5, zoom: 1 } },
    { ids: [454, 531], name: 'Ice Walker', focus: { fx: 0.43, fy: 0.5, zoom: 1 } },
    // Huevos: sin marco de foto; el recorte cuadrado abarca el grupo completo de huevos.
    { ids: [460], name: 'Spider Eggs1', rect: [398, 1160, 356, 290] },
    { ids: [461], name: 'Spider Eggs2', rect: [782, 1160, 356, 290] },
    { ids: [462], name: 'Spider Eggs3', rect: [1166, 1160, 356, 290] },
  ],
  3: [
    { ids: [480, 481], name: 'Zombie Fighter' },
    { ids: [482, 483], name: 'Resurrected Gladiator' },
    { ids: [484, 485], name: 'Ash Slaughter' },
    { ids: [487, 488], name: 'Cruel Blood Assassin', focus: { fx: 0.55, fy: 0.5, zoom: 1 } },
    { ids: [490, 491], name: 'Ruthless Lava Giant' },
    { ids: [557, 560], name: 'Sapi Queen' },
    { ids: [565], name: 'Dark Iron Knight', focus: { fx: 0.6, fy: 0.5, zoom: 1 } },
  ],
}

export type ExtraMonsterPhoto = {
  id: number
  sheet: number
  rect: PhotoRect
  sourceName: string
  focus: PhotoFocus
  /** Otros IDs que comparten exactamente esta foto (referencia de familia). */
  sharedWith: number[]
}

const DEFAULT_FOCUS: PhotoFocus = { fx: 0.5, fy: 0.5, zoom: 1 }
/** Franja de la foto en cada celda: x 12–372, y 47–250 (medido); 2 px de margen interior. */
const photoRect = (i: number): PhotoRect => [(i % 4) * CELL + 14, Math.floor(i / 4) * CELL + 49, 356, 199]

export const extraMonsterPhotos: ExtraMonsterPhoto[] = Object.entries(layout).flatMap(([sheet, cells]) =>
  cells.flatMap((c, i) =>
    c.ids.map((id) => ({
      id,
      sheet: Number(sheet),
      rect: c.rect ?? photoRect(i),
      sourceName: c.name,
      focus: c.focus ?? DEFAULT_FOCUS,
      sharedWith: c.ids.filter((o) => o !== id),
    })),
  ),
)

/** Fotos distintas (no IDs): una por celda. */
export const extraPhotoCount = Object.values(layout).reduce((n, cells) => n + cells.length, 0)

/** El recorte cae dentro de la lámina y de su celda, sin tocar el rótulo (que empieza ~300 px dentro de la celda; los huevos llegan hasta 298). */
export function extraRectInBounds(p: ExtraMonsterPhoto): boolean {
  const [x, y, w, h] = p.rect
  if (x < 0 || y < 0 || w <= 0 || h <= 0 || x + w > EXTRA_SHEET_SIZE || y + h > EXTRA_SHEET_SIZE) return false
  const cellX = Math.floor((x + w / 2) / CELL) * CELL
  const cellY = Math.floor((y + h / 2) / CELL) * CELL
  return x >= cellX && x + w <= cellX + CELL && y >= cellY && y + h <= cellY + 300
}
